import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import OwnerSidebar from "@/components/navigation/OwnerSidebar";
import AppFooter from "@/components/layout/AppFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { format, parseISO, startOfDay, subDays } from "date-fns";
import { de } from "date-fns/locale";
import { 
  Calendar as CalendarIcon, Users,
  Loader2, Copy, Check, Edit, UserPlus, ArrowUpDown,
  AlertCircle, X, Upload, User, Mail, Image, Settings, CalendarDays
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
const DEFAULT_PLAN_SETTINGS = {
  subscription_days_left: 90,
  user_can_comment_days: false,
  user_can_see_users_needed: false,
  user_can_see_other_users: false,
  user_can_see_mydetails_mail: false,
  user_can_see_mydetails_phone: false,
  user_day_canwork_deadline_in_days: -1,
  multiple_sessions_per_day: false,
  schedule_show_times: false,
  user_see_schedule_comment: false,
  schedule_comment: "",
};

const DienstplanPage = ({ mode = "dashboard" }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isDashboardMode = mode === "dashboard";
  const isEventsMode = mode === "veranstaltungen";
  const isSettingsMode = mode === "einstellungen";
  
  const [dienstplan, setDienstplan] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [myAssignments, setMyAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Create shift state
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [shiftDates, setShiftDates] = useState([]);  // Multiple dates
  const [shiftName, setShiftName] = useState("");
  const [shiftDescription, setShiftDescription] = useState("");
  const [shiftStartTime, setShiftStartTime] = useState("");
  const [shiftEndTime, setShiftEndTime] = useState("");
  const [shiftPosition, setShiftPosition] = useState("");
  const [shiftLocation, setShiftLocation] = useState("");
  const [shiftRequiredStaff, setShiftRequiredStaff] = useState(1);
  
  // Edit shift state
  const [editOpen, setEditOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [editShiftDate, setEditShiftDate] = useState(null);
  
  // Invite link state
  const [inviteCopied, setInviteCopied] = useState(false);
  
  // Assignment view state
  const [selectedShift, setSelectedShift] = useState(null);
  const [assignmentsOpen, setAssignmentsOpen] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  // Events table state
  const [eventsFilter, setEventsFilter] = useState("");
  const [eventsSortBy, setEventsSortBy] = useState("date");
  const [eventsSortDir, setEventsSortDir] = useState("asc");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shiftToDelete, setShiftToDelete] = useState(null);
  const [deletingShiftId, setDeletingShiftId] = useState(null);
  const [votingShiftId, setVotingShiftId] = useState(null);
  const [deletePlanDialogOpen, setDeletePlanDialogOpen] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState(false);
  
  const [dienstplanName, setDienstplanName] = useState("");
  const [responsibleName, setResponsibleName] = useState("");
  const [responsibleEmail, setResponsibleEmail] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [planSettings, setPlanSettings] = useState(DEFAULT_PLAN_SETTINGS);

  const applyPlanSettings = useCallback((settingsData) => {
    if (!settingsData) {
      setPlanSettings(DEFAULT_PLAN_SETTINGS);
      return;
    }
    setPlanSettings({
      subscription_days_left: Number(settingsData.subscription_days_left ?? 90),
      user_can_comment_days: Boolean(settingsData.user_can_comment_days),
      user_can_see_users_needed: Boolean(settingsData.user_can_see_users_needed),
      user_can_see_other_users: Boolean(settingsData.user_can_see_other_users),
      user_can_see_mydetails_mail: Boolean(settingsData.user_can_see_mydetails_mail),
      user_can_see_mydetails_phone: Boolean(settingsData.user_can_see_mydetails_phone),
      user_day_canwork_deadline_in_days: Number(settingsData.user_day_canwork_deadline_in_days ?? 0) <= 0
        ? -1
        : Math.min(Math.max(Number(settingsData.user_day_canwork_deadline_in_days), 1), 30),
      multiple_sessions_per_day: Boolean(settingsData.multiple_sessions_per_day),
      schedule_show_times: Boolean(settingsData.schedule_show_times),
      user_see_schedule_comment: Boolean(settingsData.user_see_schedule_comment),
      schedule_comment: settingsData.schedule_comment || "",
    });
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dpRes, shiftsRes, membersRes, statsRes, assignmentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/dienstplan/${id}`),
        axios.get(`${API_URL}/api/shifts/${id}`),
        axios.get(`${API_URL}/api/dienstplan/${id}/members`),
        axios.get(`${API_URL}/api/stats/${id}`),
        axios.get(`${API_URL}/api/my-assignments`)
      ]);
      setDienstplan(dpRes.data);
      setDienstplanName(dpRes.data.name || "");
      setShifts(shiftsRes.data);
      setMembers(membersRes.data);
      setStats(statsRes.data);
      setMyAssignments(assignmentsRes.data || []);
      // Set responsible data
      setResponsibleName(dpRes.data.responsible_name || "");
      setResponsibleEmail(dpRes.data.responsible_email || "");

      try {
        const settingsRes = await axios.get(`${API_URL}/api/settings/dienstplan/${id}`);
        applyPlanSettings(settingsRes.data);
      } catch (settingsError) {
        console.error("Error fetching dienstplan settings:", settingsError);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 404) {
        toast.error("Dienstplan nicht gefunden");
        navigate("/dienstplan");
      } else {
        toast.error("Fehler beim Laden der Daten");
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate, applyPlanSettings]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateShift = async (e) => {
    e.preventDefault();
    
    if (shiftDates.length === 0 || !shiftName.trim()) {
      toast.error("Bitte wähle mindestens einen Tag und gib einen Namen ein");
      return;
    }
    
    const minSelectableDate = startOfDay(new Date());
    if (shiftDates.some((d) => d < minSelectableDate)) {
      toast.error("Bitte nur Tage ab heute auswählen");
      return;
    }

    setCreating(true);
    try {
      const dates = shiftDates.map(d => format(d, "yyyy-MM-dd"));
      
      const response = await axios.post(`${API_URL}/api/shifts/bulk`, {
        dienstplan_id: id,
        dates: dates,
        name: shiftName,
        description: shiftDescription,
        start_time: shiftStartTime || null,
        end_time: shiftEndTime || null,
        event_start: shiftStartTime || null,
        event_end: shiftEndTime || null,
        position: shiftPosition,
        location: shiftLocation,
        required_staff: parseInt(shiftRequiredStaff)
      });
      
      setShifts([...shifts, ...response.data].sort((a, b) => a.date.localeCompare(b.date)));
      setCreateOpen(false);
      resetShiftForm();
      toast.success(`${response.data.length} Schicht(en) erstellt!`);
      
      // Refresh stats
      const statsRes = await axios.get(`${API_URL}/api/stats/${id}`);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error creating shift:", error);
      toast.error("Fehler beim Erstellen der Schicht");
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateShift = async (e) => {
    e.preventDefault();
    
    if (!editingShift) return;
    
    setCreating(true);
    try {
      const response = await axios.put(`${API_URL}/api/shifts/${editingShift.id}`, {
        name: shiftName,
        description: shiftDescription,
        date: format(editShiftDate, "yyyy-MM-dd"),
        start_time: shiftStartTime || null,
        end_time: shiftEndTime || null,
        event_start: shiftStartTime || null,
        event_end: shiftEndTime || null,
        position: shiftPosition,
        location: shiftLocation,
        required_staff: parseInt(shiftRequiredStaff)
      });
      
      setShifts(shifts.map(s => s.id === editingShift.id ? response.data : s)
        .sort((a, b) => a.date.localeCompare(b.date)));
      setEditOpen(false);
      setEditingShift(null);
      resetShiftForm();
      toast.success("Schicht aktualisiert!");
    } catch (error) {
      console.error("Error updating shift:", error);
      toast.error("Fehler beim Aktualisieren der Schicht");
    } finally {
      setCreating(false);
    }
  };

  const openDeleteShiftDialog = (shift) => {
    setShiftToDelete(shift);
    setDeleteDialogOpen(true);
  };

  const handleDeleteShift = async () => {
    if (!shiftToDelete) return;
    const shiftId = shiftToDelete.id;
    setDeletingShiftId(shiftId);

    try {
      await axios.delete(`${API_URL}/api/shifts/${shiftId}`);
      setShifts((prev) => prev.filter((s) => s.id !== shiftId));
      toast.success("Schicht gelöscht");
      setDeleteDialogOpen(false);
      setShiftToDelete(null);
      
      // Refresh stats
      const statsRes = await axios.get(`${API_URL}/api/stats/${id}`);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error deleting shift:", error);
      toast.error("Fehler beim Löschen der Schicht");
    } finally {
      setDeletingShiftId(null);
    }
  };

  const isAssigned = (shiftId) => myAssignments.some((assignment) => assignment.shift_id === shiftId);

  const getAssignment = (shiftId) => myAssignments.find((assignment) => assignment.shift_id === shiftId);

  const handleToggleAssignment = async (shift) => {
    if (!shift?.id) return;

    setVotingShiftId(shift.id);
    try {
      const existingAssignment = getAssignment(shift.id);
      if (existingAssignment?.id) {
        await axios.delete(`${API_URL}/api/assignments/${existingAssignment.id}`);
        toast.success("Zusage zurückgenommen");
      } else {
        await axios.post(`${API_URL}/api/assignments`, { shift_id: shift.id });
        toast.success("Zusage erfolgreich");
      }

      const [assignmentsRes, shiftsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/my-assignments`),
        axios.get(`${API_URL}/api/shifts/${id}`),
        axios.get(`${API_URL}/api/stats/${id}`),
      ]);
      setMyAssignments(assignmentsRes.data || []);
      setShifts(shiftsRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error toggling assignment:", error);
      toast.error(error.response?.data?.detail || "Fehler bei der Zusage");
    } finally {
      setVotingShiftId(null);
    }
  };

  const openEditModal = (shift) => {
    setEditingShift(shift);
    setEditShiftDate(parseISO(shift.date));
    setShiftName(shift.name);
    setShiftDescription(shift.description);
    setShiftStartTime(shift.start_time || shift.event_start || "");
    setShiftEndTime(shift.end_time || shift.event_end || "");
    setShiftPosition(shift.position);
    setShiftLocation(shift.location || "");
    setShiftRequiredStaff(shift.required_staff);
    setEditOpen(true);
  };

  const resetShiftForm = () => {
    setShiftDates([]);
    setEditShiftDate(null);
    setShiftName("");
    setShiftDescription("");
    setShiftStartTime("");
    setShiftEndTime("");
    setShiftPosition("");
    setShiftLocation("");
    setShiftRequiredStaff(1);
  };

  const copyInviteLink = () => {
    const inviteUrl = `${window.location.origin}/invite/${dienstplan?.invite_code}`;
    navigator.clipboard.writeText(inviteUrl);
    setInviteCopied(true);
    toast.success("Einladungs-Link kopiert!");
    setTimeout(() => setInviteCopied(false), 2000);
  };

  const viewAssignments = async (shift) => {
    setSelectedShift(shift);
    setAssignmentsOpen(true);
    setLoadingAssignments(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/assignments/${shift.id}`);
      setAssignments(response.data);
    } catch (error) {
      console.error("Error fetching assignments:", error);
      toast.error("Fehler beim Laden der Zusagen");
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Erfolgreich abgemeldet");
  };

  const getShiftStatus = (shift) => {
    if (shift.confirmed_count >= shift.required_staff) return "filled";
    if (shift.confirmed_count > 0) return "partial";
    return "open";
  };

  const getStatusBadge = (shift) => {
    const status = getShiftStatus(shift);
    if (status === "filled") {
      return <Badge className="bg-[#17c671]/10 text-[#17c671] border-[#17c671]/30">Besetzt</Badge>;
    }
    if (status === "partial") {
      return <Badge className="bg-amber-50 text-amber-600 border-amber-200">Teilweise besetzt</Badge>;
    }
    return <Badge className="bg-red-50 text-red-600 border-red-200">Offen</Badge>;
  };

  const handleEventsHeaderSort = (field) => {
    if (eventsSortBy === field) {
      setEventsSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setEventsSortBy(field);
    setEventsSortDir("asc");
  };

  const getHeaderSortIconClass = (field) => {
    if (eventsSortBy !== field) return "text-gray-300";
    return eventsSortDir === "asc" ? "text-[#17c671] rotate-180" : "text-[#17c671]";
  };

  const visibleFromDate = subDays(startOfDay(new Date()), 7);
  const visibleShifts = shifts.filter((shift) => {
    const shiftDate = parseISO(shift.date);
    return !Number.isNaN(shiftDate.getTime()) && shiftDate >= visibleFromDate;
  });
  const today = startOfDay(new Date());
  const upcomingShifts = [...shifts]
    .filter((shift) => {
      const shiftDate = parseISO(shift.date);
      return !Number.isNaN(shiftDate.getTime()) && shiftDate >= today;
    })
    .sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      const aStart = (a.start_time || a.event_start || "23:59").toString();
      const bStart = (b.start_time || b.event_start || "23:59").toString();
      return aStart.localeCompare(bStart);
    })
    .slice(0, 6);

  const processedShifts = [...visibleShifts]
    .filter((shift) => {
      if (!eventsFilter.trim()) return true;
      const query = eventsFilter.trim().toLowerCase();
      const dateLabel = format(parseISO(shift.date), "dd.MM.yyyy EEE", { locale: de }).toLowerCase();
      return (
        (shift.name || "").toLowerCase().includes(query) ||
        dateLabel.includes(query)
      );
    })
    .sort((a, b) => {
      let aVal;
      let bVal;
      switch (eventsSortBy) {
        case "name":
          aVal = (a.name || "").toLowerCase();
          bVal = (b.name || "").toLowerCase();
          break;
        case "time":
          aVal = `${a.start_time || a.event_start || ""}-${a.end_time || a.event_end || ""}`.toLowerCase();
          bVal = `${b.start_time || b.event_start || ""}-${b.end_time || b.event_end || ""}`.toLowerCase();
          break;
        case "required_staff":
          aVal = Number(a.required_staff || 0);
          bVal = Number(b.required_staff || 0);
          break;
        case "confirmed_count":
          aVal = Number(a.confirmed_count || 0);
          bVal = Number(b.confirmed_count || 0);
          break;
        case "date":
        default:
          aVal = a.date;
          bVal = b.date;
          break;
      }
      if (aVal === bVal) return 0;
      const direction = eventsSortDir === "asc" ? 1 : -1;
      return aVal > bVal ? direction : -direction;
    });

  const handleSaveResponsible = async () => {
    const nextName = (dienstplanName || "").trim();
    if (!nextName) {
      toast.error("Dienstplanname darf nicht leer sein");
      return;
    }

    setSavingSettings(true);
    try {
      const baseResponse = await axios.put(`${API_URL}/api/dienstplan/${id}`, {
        name: nextName,
        responsible_name: responsibleName,
        responsible_email: responsibleEmail
      });

      const settingsPayload = {
        name: nextName,
        user_can_comment_days: planSettings.user_can_comment_days,
        user_can_see_users_needed: planSettings.user_can_see_users_needed,
        user_can_see_other_users: planSettings.user_can_see_other_users,
        user_can_see_mydetails_mail: planSettings.user_can_see_mydetails_mail,
        user_can_see_mydetails_phone: planSettings.user_can_see_mydetails_phone,
        user_day_canwork_deadline_in_days: Number(planSettings.user_day_canwork_deadline_in_days),
        multiple_sessions_per_day: planSettings.multiple_sessions_per_day,
        schedule_show_times: planSettings.multiple_sessions_per_day ? true : planSettings.schedule_show_times,
        user_see_schedule_comment: planSettings.user_see_schedule_comment,
        schedule_comment: planSettings.schedule_comment || "",
      };
      const settingsResponse = await axios.put(`${API_URL}/api/settings/dienstplan/${id}`, settingsPayload);

      setDienstplan(baseResponse.data);
      setDienstplanName(baseResponse.data?.name || nextName);
      applyPlanSettings(settingsResponse.data);
      toast.success("Einstellungen gespeichert");
    } catch (error) {
      console.error("Error saving responsible:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSavingSettings(false);
    }
  };

  // Upload image
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(
        `${API_URL}/api/dienstplan/${id}/upload-image`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      
      setDienstplan({ ...dienstplan, image_url: response.data.image_url });
      toast.success("Bild hochgeladen");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Hochladen");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteDienstplan = async () => {
    setDeletingPlan(true);
    try {
      await axios.delete(`${API_URL}/api/dienstplan/${id}`);
      toast.success("Dienstplan gelöscht");
      setDeletePlanDialogOpen(false);
      navigate("/dienstplan", { replace: true });
    } catch (error) {
      console.error("Error deleting dienstplan:", error);
      toast.error("Fehler beim Löschen des Dienstplans");
    } finally {
      setDeletingPlan(false);
    }
  };

  const updatePlanSetting = (key, value) => {
    setPlanSettings((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "multiple_sessions_per_day" && value) {
        next.schedule_show_times = true;
      }
      return next;
    });
  };

  const pageTitle = isSettingsMode ? "Einstellungen" : (isEventsMode ? "Veranstaltungen" : "Dashboard");

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#17c671]" />
        </div>
        <AppFooter />
      </div>
    );
  }

  return (
    <div className="dashboard-layout" data-testid="dienstplan-page">
      {/* Mobile Sidebar Overlay */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />
      
      <OwnerSidebar
        user={user}
        sidebarOpen={sidebarOpen}
        activeMain="dashboard"
        currentPlanId={id}
        currentPlanSection={isSettingsMode ? "settings" : (isEventsMode ? "events" : "dashboard")}
        onLogout={handleLogout}
      />

      {/* Main Content - Light */}
      <main className="dashboard-main">
        <div className="max-w-5xl mx-auto px-6 py-8 flex-1 w-full">
          <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-1">
              <h1 className="font-heading text-2xl font-semibold text-[#212529]">
                {dienstplan?.name} - {pageTitle}
              </h1>
              {isEventsMode ? (
                <p className="text-gray-500">Hier erstellst und bearbeitest du deine Veranstaltungen</p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {isSettingsMode && (
                <Button
                  type="button"
                  variant="outline"
                  className="border-gray-300"
                  onClick={() => navigate(`/dienstplan/${id}`)}
                  data-testid="dienstplan-settings-back-btn"
                >
                  Zurück
                </Button>
              )}
              {!isSettingsMode && (
                <Button
                  type="button"
                  variant="outline"
                  className="border-gray-300"
                  onClick={() => navigate(`/dienstplan/${id}/einstellungen`)}
                  data-testid="dienstplan-settings-btn"
                >
                  <Settings className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Einstellungen</span>
                </Button>
              )}

              {isDashboardMode && (
                <Button 
                  variant="outline" 
                  onClick={copyInviteLink}
                  className="border-gray-300"
                  data-testid="copy-invite-btn"
                >
                  {inviteCopied ? (
                    <>
                      <Check className="w-4 h-4 sm:mr-2 text-[#17c671]" />
                      <span className="hidden sm:inline">Kopiert!</span>
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Team einladen</span>
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
          {isSettingsMode && (
            <div className="max-w-5xl space-y-6 animate-fade-in">
              <Card className="border-gray-200 shadow-sm">
                <CardHeader className="pb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#17c671]/10 rounded-lg flex items-center justify-center">
                      <Settings className="w-5 h-5 text-[#17c671]" />
                    </div>
                    <div>
                      <CardTitle className="font-heading text-[#212529]">Dienstplan-Einstellungen</CardTitle>
                      <p className="text-sm text-gray-500 mt-1">Verwalte hier deinen Dienstplan</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-xl border border-gray-200 bg-[#f8fafc] p-4">
                    <p className="text-sm font-semibold text-[#212529]">Abonnement</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Der Dienstplan läuft in {planSettings.subscription_days_left} Tagen ab.
                    </p>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-[#f8fafc] p-4 flex items-center sm:justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      className="border-gray-300 w-full sm:w-auto"
                      onClick={() => window.location.assign("https://www.skcloudservice.de/shop")}
                    >
                      Verlänge hier dein Abonnement
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-gray-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-[#212529]">Stammdaten</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="dienstplan-name-settings" className="text-[#212529]">
                      Dienstplanname
                    </Label>
                    <Input
                      id="dienstplan-name-settings"
                      value={dienstplanName}
                      onChange={(e) => setDienstplanName(e.target.value)}
                      className="border-gray-300 focus:border-[#17c671]"
                      data-testid="dienstplan-settings-name-input"
                    />
                  </div>

                  <div className="space-y-3">
                    <Label className="text-[#212529] flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      Dienstplan-Bild
                    </Label>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-xl border border-gray-200 bg-[#f8fafc] p-4">
                      {dienstplan?.image_url ? (
                        <img
                          src={`${API_URL}${dienstplan.image_url}`}
                          alt="Dienstplan"
                          className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                          <Image className="w-8 h-8 text-gray-300" />
                        </div>
                      )}
                      <div>
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            onChange={handleImageUpload}
                            className="hidden"
                            data-testid="image-upload-input"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            className="border-gray-300"
                            disabled={uploadingImage}
                            asChild
                          >
                            <span>
                              {uploadingImage ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Upload className="w-4 h-4 mr-2" />
                              )}
                              Bild hochladen
                            </span>
                          </Button>
                        </label>
                        <p className="text-xs text-gray-500 mt-2">JPEG, PNG, WebP, GIF</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-[#212529] flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Verantwortlicher
                    </Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="resp-name" className="text-sm text-gray-600">Name</Label>
                        <Input
                          id="resp-name"
                          placeholder="Max Mustermann"
                          value={responsibleName}
                          onChange={(e) => setResponsibleName(e.target.value)}
                          className="mt-1 border-gray-300 focus:border-[#17c671]"
                          data-testid="responsible-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="resp-email" className="text-sm text-gray-600">E-Mail</Label>
                        <Input
                          id="resp-email"
                          type="email"
                          placeholder="max@beispiel.de"
                          value={responsibleEmail}
                          onChange={(e) => setResponsibleEmail(e.target.value)}
                          className="mt-1 border-gray-300 focus:border-[#17c671]"
                          data-testid="responsible-email-input"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-gray-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-[#212529]">Mitarbeiter Einstellungen</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white p-3">
                    <Label htmlFor="set-comment-days">Mitarbeiter können Tage kommentieren</Label>
                    <Switch
                      id="set-comment-days"
                      checked={planSettings.user_can_comment_days}
                      onCheckedChange={(val) => updatePlanSetting("user_can_comment_days", val)}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white p-3">
                    <Label htmlFor="set-see-needed">Mitarbeiter können benötigtes Personal sehen</Label>
                    <Switch
                      id="set-see-needed"
                      checked={planSettings.user_can_see_users_needed}
                      onCheckedChange={(val) => updatePlanSetting("user_can_see_users_needed", val)}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white p-3">
                    <Label htmlFor="set-see-others">Mitarbeiter können Zusagen anderer Mitarbeiter sehen</Label>
                    <Switch
                      id="set-see-others"
                      checked={planSettings.user_can_see_other_users}
                      onCheckedChange={(val) => updatePlanSetting("user_can_see_other_users", val)}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white p-3">
                    <Label htmlFor="set-see-mail">Mitarbeiter können meine E-Mail-Adresse sehen</Label>
                    <Switch
                      id="set-see-mail"
                      checked={planSettings.user_can_see_mydetails_mail}
                      onCheckedChange={(val) => updatePlanSetting("user_can_see_mydetails_mail", val)}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white p-3">
                    <Label htmlFor="set-see-phone">Mitarbeiter können meine Telefonnummer sehen</Label>
                    <Switch
                      id="set-see-phone"
                      checked={planSettings.user_can_see_mydetails_phone}
                      onCheckedChange={(val) => updatePlanSetting("user_can_see_mydetails_phone", val)}
                    />
                  </div>
                  <div className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-white p-3">
                    <Label htmlFor="set-same-day">Mitarbeiter können am selben Tag zusagen/absagen</Label>
                    <Select
                      value={String(planSettings.user_day_canwork_deadline_in_days)}
                      onValueChange={(value) =>
                        updatePlanSetting(
                          "user_day_canwork_deadline_in_days",
                          value === "-1" ? -1 : Number(value),
                        )
                      }
                    >
                      <SelectTrigger id="set-same-day" className="w-full border-gray-300 focus:border-[#17c671]">
                        <SelectValue placeholder="Frist wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="-1">Unendlich</SelectItem>
                        {Array.from({ length: 30 }, (_, index) => {
                          const day = index + 1;
                          return (
                            <SelectItem key={day} value={String(day)}>
                              {day}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-gray-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-[#212529]">Dienstplan Einstellungen</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white p-3">
                    <Label htmlFor="set-multi-day">Mehrere Veranstaltungen an einem Tag (nur mit Uhrzeiten kombinierbar)</Label>
                    <Switch
                      id="set-multi-day"
                      checked={planSettings.multiple_sessions_per_day}
                      onCheckedChange={(val) => updatePlanSetting("multiple_sessions_per_day", val)}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white p-3">
                    <Label htmlFor="set-show-times">Zeige Uhrzeiten für Veranstaltungen</Label>
                    <Switch
                      id="set-show-times"
                      checked={planSettings.schedule_show_times}
                      disabled={planSettings.multiple_sessions_per_day}
                      onCheckedChange={(val) => updatePlanSetting("schedule_show_times", val)}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-gray-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-[#212529]">Hinweistext für Dienstplan</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="schedule-comment-text">Hinweistext</Label>
                    <Textarea
                      id="schedule-comment-text"
                      rows={4}
                      value={planSettings.schedule_comment}
                      onChange={(e) => updatePlanSetting("schedule_comment", e.target.value)}
                      placeholder="Hinweistext für Mitarbeiter..."
                      className="border-gray-300 focus:border-[#17c671]"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white p-3">
                    <Label htmlFor="set-show-comment">Hinweistext anzeigen für Mitarbeiter</Label>
                    <Switch
                      id="set-show-comment"
                      checked={planSettings.user_see_schedule_comment}
                      onCheckedChange={(val) => updatePlanSetting("user_see_schedule_comment", val)}
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <div className="w-full flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDeletePlanDialogOpen(true)}
                    className="border-rose-300 text-rose-600 hover:bg-rose-50 hover:border-rose-400 hover:text-rose-700"
                    data-testid="delete-dienstplan-btn"
                  >
                    Dienstplan löschen
                  </Button>
                  <Button
                    onClick={handleSaveResponsible}
                    className="bg-[#17c671] hover:bg-[#14a85d] text-white font-semibold"
                    disabled={savingSettings}
                    data-testid="save-responsible-btn"
                  >
                    {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Speichern
                  </Button>
                </div>
              </div>
            </div>
          )}
          {isDashboardMode && (
          <>
          {/* Stats Cards */}
          <div className="stats-grid mb-8">
            <Card className="stat-card animate-fade-in stagger-1">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#17c671]/10 rounded-lg flex items-center justify-center">
                  <CalendarDays className="w-6 h-6 text-[#17c671]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#212529]">{stats?.total_shifts || 0}</p>
                  <p className="text-sm text-gray-500">Schichten gesamt</p>
                </div>
              </div>
            </Card>
            <Card className="stat-card animate-fade-in stagger-2">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#17c671]/10 rounded-lg flex items-center justify-center">
                  <Check className="w-6 h-6 text-[#17c671]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#212529]">{stats?.filled_shifts || 0}</p>
                  <p className="text-sm text-gray-500">Besetzt</p>
                </div>
              </div>
            </Card>
            <Card className="stat-card animate-fade-in stagger-3">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#212529]">{stats?.open_shifts || 0}</p>
                  <p className="text-sm text-gray-500">Offen</p>
                </div>
              </div>
            </Card>
            <Card className="stat-card animate-fade-in stagger-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#212529]">{stats?.total_members || 0}</p>
                  <p className="text-sm text-gray-500">Mitarbeiter</p>
                </div>
              </div>
            </Card>
          </div>
          
          {/* Invite Code Section */}
          <Card className="mb-8 animate-fade-in stagger-5 border-gray-200">
            <CardContent className="py-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-[#212529] mb-1">Einladungs-Link</h3>
                  <p className="text-sm text-gray-500">Teile diesen Link mit deinen Mitarbeitern</p>
                </div>
                <div className="flex items-center gap-3">
                  <code className="invite-code-display text-sm">
                    {dienstplan?.invite_code}
                  </code>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={copyInviteLink}
                    className="border-gray-300"
                    data-testid="copy-link-btn"
                  >
                    {inviteCopied ? <Check className="w-4 h-4 text-[#17c671]" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Responsible Person Info (if set) */}
          {(dienstplan?.responsible_name || dienstplan?.responsible_email) && (
            <Card className="mb-8 animate-fade-in border-gray-200">
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  {dienstplan?.image_url && (
                    <img 
                      src={`${API_URL}${dienstplan.image_url}`}
                      alt="Dienstplan" 
                      className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                    />
                  )}
                  <div>
                    <p className="text-sm text-gray-500">Verantwortlicher</p>
                    <p className="font-medium text-[#212529]">{dienstplan?.responsible_name}</p>
                    {dienstplan?.responsible_email && (
                      <a href={`mailto:${dienstplan.responsible_email}`} className="text-sm text-[#17c671] hover:underline flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {dienstplan.responsible_email}
                      </a>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="mb-8 animate-fade-in border-gray-200">
            <CardHeader>
              <CardTitle className="font-heading text-[#212529]">Nächste 6 Veranstaltungen</CardTitle>
            </CardHeader>
            <CardContent>
              {upcomingShifts.length === 0 ? (
                <p className="text-sm text-gray-500">Keine kommenden Veranstaltungen vorhanden.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="dienstplan-upcoming-events-grid">
                  {upcomingShifts.map((shift) => {
                    const parsedDate = parseISO(shift.date);
                    const dateLabel = format(parsedDate, "EEE, dd.MM.yyyy", { locale: de });
                    const startTime = shift.start_time || shift.event_start;
                    const endTime = shift.end_time || shift.event_end;
                    const timeLabel = startTime || endTime ? `${startTime || "--:--"} - ${endTime || "--:--"}` : "Ohne Uhrzeit";
                    const confirmedCount = Number(shift.confirmed_count || 0);

                    return (
                      <div
                        key={shift.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => viewAssignments(shift)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            viewAssignments(shift);
                          }
                        }}
                        className="rounded-xl border border-gray-200 bg-white p-4 cursor-pointer transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#17c671]/40"
                        data-testid={`dienstplan-upcoming-event-${shift.id}`}
                      >
                        <p className="font-semibold text-[#212529] break-words">{shift.name}</p>
                        <p className="text-sm text-gray-500 mt-1">{dateLabel}</p>
                        <p className="text-sm text-gray-500">{timeLabel}</p>
                        <div className="mt-3 flex items-center justify-between text-sm">
                          <span className="text-gray-500">Zusagen</span>
                          <span className="font-medium text-[#212529]">{confirmedCount}/{shift.required_staff}</span>
                        </div>
                        <div className="mt-3">
                          {getStatusBadge(shift)}
                        </div>
                        <div className="mt-3">
                          <Button
                            type="button"
                            variant={isAssigned(shift.id) ? "outline" : "default"}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleToggleAssignment(shift);
                            }}
                            className={isAssigned(shift.id)
                              ? "w-full border-gray-300"
                              : "w-full bg-[#17c671] hover:bg-[#14a85d] text-white"}
                            disabled={votingShiftId === shift.id}
                          >
                            {votingShiftId === shift.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4 mr-2" />
                            )}
                            {isAssigned(shift.id) ? "Zusage zurücknehmen" : "Jetzt zusagen"}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
          </>
          )}
          
          {isEventsMode && (
          <>
          <div className="mb-8">
            <div className="flex justify-center mt-8">
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-[#17c671] hover:bg-[#14a85d] text-white font-medium rounded-full px-9 h-12 min-w-[260px]"
                    data-testid="create-shift-btn"
                  >
                    neuen Tag hinzufügen
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-3xl bg-white border-gray-200">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-[#212529]">Neuen Tag hinzufügen</DialogTitle>
                    <DialogDescription className="text-gray-500">
                      Wähle mehrere Tage ab heute und setze Name sowie Mindestpersonal.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateShift} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-[#212529]">Tage auswählen * (Mehrfachauswahl möglich)</Label>
                      <div className="border border-gray-200 rounded-lg p-3 bg-[#f8fafc]">
                        <Calendar
                          mode="multiple"
                          selected={shiftDates}
                          onSelect={setShiftDates}
                          locale={de}
                          disabled={(date) => date < startOfDay(new Date())}
                          className="w-full p-0"
                          classNames={{
                            months: "w-full",
                            month: "w-full space-y-4",
                            table: "w-full border-collapse",
                            head_row: "grid grid-cols-7",
                            head_cell: "text-muted-foreground h-9 text-center font-normal text-sm",
                            row: "grid grid-cols-7 mt-2",
                            cell: "p-1 text-center",
                            day: "h-14 w-full rounded-full p-0 font-normal hover:bg-gray-100 aria-selected:bg-[#17c671] aria-selected:text-white",
                            day_today: "rounded-full bg-gray-100 text-gray-900 border border-gray-200 aria-selected:bg-gray-100 aria-selected:text-gray-900 after:content-['Heute'] after:block after:text-[10px] after:leading-none after:text-gray-500 after:mt-0.5",
                          }}
                          data-testid="shift-calendar"
                        />
                        {shiftDates.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-sm text-gray-600 mb-2">
                              <strong>{shiftDates.length}</strong> Tag(e) ausgewählt:
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {shiftDates.sort((a, b) => a - b).map((date, i) => (
                                <Badge key={i} variant="secondary" className="text-xs bg-[#17c671]/10 text-[#17c671] border-[#17c671]/30">
                                  {format(date, "dd.MM.", { locale: de })}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="shift-name">Name *</Label>
                        <Input
                          id="shift-name"
                          placeholder="z.B. Sommerfest"
                          value={shiftName}
                          onChange={(e) => setShiftName(e.target.value)}
                          data-testid="shift-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="shift-staff">Mindestens Personal *</Label>
                        <Input
                          id="shift-staff"
                          type="number"
                          min="1"
                          value={shiftRequiredStaff}
                          onChange={(e) => setShiftRequiredStaff(e.target.value)}
                          data-testid="shift-staff-input"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                      <Button type="button" variant="outline" onClick={() => { setCreateOpen(false); resetShiftForm(); }} className="border-gray-300">
                        Abbrechen
                      </Button>
                      <Button
                        type="submit"
                        className="bg-[#17c671] hover:bg-[#14a85d] text-white font-semibold"
                        disabled={creating}
                        data-testid="shift-create-submit-btn"
                      >
                        {creating ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Erstellen...
                          </>
                        ) : (
                          shiftDates.length > 1 ? `${shiftDates.length} Tage erstellen` : "Tag erstellen"
                        )}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex flex-col lg:flex-row gap-2 lg:items-center lg:justify-between">
              <Input
                value={eventsFilter}
                onChange={(e) => setEventsFilter(e.target.value)}
                placeholder="Filter nach Name oder Datum..."
                className="w-full lg:max-w-sm border-gray-300 h-9"
                data-testid="events-filter-input"
              />
              <p className="text-xs text-gray-500">Sortierung: Spaltenkopf anklicken</p>
            </div>

            <div className="hidden md:block rounded-xl border border-gray-200 bg-white shadow-sm overflow-x-auto overflow-y-hidden">
              <table className="w-full" data-testid="shifts-table">
                <thead>
                  <tr className="bg-[#f7f8fa] border-b border-gray-200">
                    <th className="w-[96px] px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-[#111827]">Bearbeiten</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-[#111827]">
                      <button
                        type="button"
                        onClick={() => handleEventsHeaderSort("date")}
                        className="inline-flex items-center gap-1 hover:text-[#17c671]"
                        data-testid="events-header-sort-date"
                      >
                        Tag
                        <ArrowUpDown className={`w-3.5 h-3.5 transition-transform ${getHeaderSortIconClass("date")}`} />
                      </button>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-[#111827]">
                      <button
                        type="button"
                        onClick={() => handleEventsHeaderSort("name")}
                        className="inline-flex items-center gap-1 hover:text-[#17c671]"
                        data-testid="events-header-sort-name"
                      >
                        Veranstaltungsname
                        <ArrowUpDown className={`w-3.5 h-3.5 transition-transform ${getHeaderSortIconClass("name")}`} />
                      </button>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-[#111827]">
                      <button
                        type="button"
                        onClick={() => handleEventsHeaderSort("required_staff")}
                        className="inline-flex items-center gap-1 hover:text-[#17c671]"
                        data-testid="events-header-sort-required-staff"
                      >
                        min. Personal
                        <ArrowUpDown className={`w-3.5 h-3.5 transition-transform ${getHeaderSortIconClass("required_staff")}`} />
                      </button>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-[#111827]">
                      <button
                        type="button"
                        onClick={() => handleEventsHeaderSort("confirmed_count")}
                        className="inline-flex items-center gap-1 hover:text-[#17c671]"
                        data-testid="events-header-sort-confirmed"
                      >
                        Zusagen
                        <ArrowUpDown className={`w-3.5 h-3.5 transition-transform ${getHeaderSortIconClass("confirmed_count")}`} />
                      </button>
                    </th>
                    <th className="w-[96px] px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-[#111827]">Löschen</th>
                  </tr>
                </thead>
                <tbody>
                  {processedShifts.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                        Keine passenden Veranstaltungen gefunden.
                      </td>
                    </tr>
                  ) : (
                    processedShifts.map((shift) => {
                      const confirmedCount = Number(shift.confirmed_count || 0);
                      const parsedDate = parseISO(shift.date);
                      const weekday = format(parsedDate, "EEE", { locale: de }).replace(".", "");
                      const weekdayLabel = weekday.charAt(0).toUpperCase() + weekday.slice(1);
                      const dayLabel = format(parsedDate, "d", { locale: de });
                      const month = format(parsedDate, "MMM", { locale: de }).replace(".", "");
                      const monthLabel = month.charAt(0).toUpperCase() + month.slice(1);

                      return (
                        <tr key={shift.id} className="border-t border-gray-200">
                          <td className="px-3 py-4 text-center align-middle">
                            <Button
                              onClick={() => openEditModal(shift)}
                              className="h-9 w-9 p-0 rounded-full bg-[#17c671] hover:bg-[#14a85d] text-white"
                              data-testid={`edit-shift-table-${shift.id}`}
                              aria-label="Veranstaltung bearbeiten"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </td>
                          <td className="px-4 py-4 text-center align-middle">
                            <div className="inline-flex flex-col items-center leading-none gap-0.5">
                              <span className="text-xs text-gray-500 mb-0.5">{weekdayLabel}</span>
                              <span className="text-3xl font-bold text-[#111827]">{dayLabel}</span>
                              <span className="text-base text-gray-500 mt-0.5">{monthLabel}</span>
                              {(shift.start_time || shift.event_start || shift.end_time || shift.event_end) && (
                                <span className="mt-3 text-sm font-medium text-slate-500">
                                  {`${shift.start_time || shift.event_start || "--:--"} - ${shift.end_time || shift.event_end || "--:--"}`}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-center align-middle text-base text-[#374151]">
                            {shift.name}
                          </td>
                          <td className="px-4 py-4 text-center align-middle text-base text-[#374151]">
                            {shift.required_staff}
                          </td>
                          <td className="px-4 py-4 text-center align-middle">
                            <Button
                              variant="outline"
                              onClick={() => viewAssignments(shift)}
                              className="h-9 min-w-[132px] rounded-full border-2 border-amber-400 text-amber-500 hover:bg-amber-50 hover:border-amber-500 hover:text-amber-600 transition-colors text-sm font-medium"
                              data-testid={`assignments-shift-table-${shift.id}`}
                            >
                              <Users className="w-3.5 h-3.5 mr-1.5" />
                              {`${confirmedCount} Zusagen`}
                            </Button>
                            <Button
                              type="button"
                              variant={isAssigned(shift.id) ? "outline" : "default"}
                              onClick={() => handleToggleAssignment(shift)}
                              className={isAssigned(shift.id)
                                ? "h-9 min-w-[160px] rounded-full border-gray-300 mt-2"
                                : "h-9 min-w-[160px] rounded-full bg-[#17c671] hover:bg-[#14a85d] text-white mt-2"}
                              disabled={votingShiftId === shift.id}
                            >
                              {votingShiftId === shift.id ? (
                                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                              ) : (
                                <Check className="w-3.5 h-3.5 mr-1.5" />
                              )}
                              {isAssigned(shift.id) ? "Zusage zurücknehmen" : "Zusagen"}
                            </Button>
                          </td>
                          <td className="px-3 py-4 text-center align-middle">
                            <Button
                              variant="outline"
                              onClick={() => openDeleteShiftDialog(shift)}
                              className="h-9 w-9 p-0 rounded-full border-2 border-rose-300 text-rose-500 hover:bg-rose-50 hover:border-rose-400 hover:text-rose-600 transition-colors"
                              data-testid={`delete-shift-table-${shift.id}`}
                              aria-label="Veranstaltung löschen"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-3">
              {processedShifts.length === 0 ? (
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
                  Keine passenden Veranstaltungen gefunden.
                </div>
              ) : (
                processedShifts.map((shift) => {
                  const confirmedCount = Number(shift.confirmed_count || 0);
                  const parsedDate = parseISO(shift.date);
                  const weekday = format(parsedDate, "EEE", { locale: de }).replace(".", "");
                  const weekdayLabel = weekday.charAt(0).toUpperCase() + weekday.slice(1);
                  const dayLabel = format(parsedDate, "d", { locale: de });
                  const month = format(parsedDate, "MMM", { locale: de }).replace(".", "");
                  const monthLabel = month.charAt(0).toUpperCase() + month.slice(1);
                  const timeLabel = shift.start_time || shift.event_start || shift.end_time || shift.event_end
                    ? `${shift.start_time || shift.event_start || "--:--"} - ${shift.end_time || shift.event_end || "--:--"}`
                    : null;

                  return (
                    <div
                      key={shift.id}
                      className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                      data-testid={`shift-mobile-row-${shift.id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="min-w-[72px] text-center">
                          <p className="text-xs text-gray-500">{weekdayLabel}</p>
                          <p className="text-4xl font-bold leading-none text-[#111827] mt-1">{dayLabel}</p>
                          <p className="text-lg text-gray-500 mt-1">{monthLabel}</p>
                          {timeLabel && <p className="text-sm font-medium text-slate-500 mt-2">{timeLabel}</p>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-base font-semibold text-[#212529] break-words">{shift.name}</p>
                          <p className="text-sm text-gray-500 mt-2">min. Personal: {shift.required_staff}</p>
                          <p className="text-sm text-gray-500">Zusagen: {confirmedCount}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-2 mt-4">
                        <Button
                          onClick={() => openEditModal(shift)}
                          className="h-9 w-9 p-0 justify-self-start rounded-full bg-[#17c671] hover:bg-[#14a85d] text-white"
                          data-testid={`edit-shift-mobile-${shift.id}`}
                          aria-label="Veranstaltung bearbeiten"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => viewAssignments(shift)}
                          className="h-9 px-4 rounded-full border-2 border-amber-400 text-amber-500 hover:bg-amber-50 hover:border-amber-500 hover:text-amber-600 transition-colors text-sm font-medium"
                          data-testid={`assignments-shift-mobile-${shift.id}`}
                        >
                          <Users className="w-3.5 h-3.5 mr-1.5" />
                          {`${confirmedCount} Zusagen`}
                        </Button>
                        <Button
                          type="button"
                          variant={isAssigned(shift.id) ? "outline" : "default"}
                          onClick={() => handleToggleAssignment(shift)}
                          className={isAssigned(shift.id)
                            ? "h-9 px-3 rounded-full border-gray-300"
                            : "h-9 px-3 rounded-full bg-[#17c671] hover:bg-[#14a85d] text-white"}
                          disabled={votingShiftId === shift.id}
                        >
                          {votingShiftId === shift.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Check className="w-3.5 h-3.5" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => openDeleteShiftDialog(shift)}
                          className="h-9 w-9 p-0 justify-self-end rounded-full border-2 border-rose-300 text-rose-500 hover:bg-rose-50 hover:border-rose-400 hover:text-rose-600 transition-colors"
                          data-testid={`delete-shift-mobile-${shift.id}`}
                          aria-label="Veranstaltung löschen"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          </>
          )}
          
          {/* Members Section */}
          {isDashboardMode && members.length > 0 && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl font-semibold text-[#212529]">
                  Personal ({members.filter(m => !m.is_blocked).length})
                </h2>
                <Link 
                  to={`/dienstplan/${id}/personal`}
                  className="text-sm text-[#17c671] hover:underline flex items-center gap-1"
                  data-testid="manage-team-link"
                >
                  <Users className="w-4 h-4" />
                  Personal verwalten
                </Link>
              </div>
              <Card className="border-gray-200">
                <CardContent className="p-0">
                  {members.filter(m => !m.is_blocked).slice(0, 5).map((member, index) => (
                    <div key={member.id} className={`member-item ${index > 0 ? 'border-t border-gray-100' : ''}`}>
                      <div className="w-10 h-10 bg-[#17c671]/10 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-[#17c671]">
                          {member.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-[#212529]">{member.name}</p>
                        <p className="text-sm text-gray-500">{member.email}</p>
                      </div>
                    </div>
                  ))}
                  {members.filter(m => !m.is_blocked).length > 5 && (
                    <Link 
                      to={`/dienstplan/${id}/personal`}
                      className="block px-4 py-3 text-center text-sm text-[#17c671] hover:bg-gray-50 border-t border-gray-100"
                    >
                      Alle {members.filter(m => !m.is_blocked).length} Mitglieder anzeigen
                    </Link>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>
      
      {/* Edit Shift Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#212529]">Schicht bearbeiten</DialogTitle>
            <DialogDescription className="text-gray-500">
              Aendere die Details der Schicht.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateShift} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#212529]">Datum *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal border-gray-300">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {editShiftDate ? format(editShiftDate, "PPP", { locale: de }) : "Datum wählen"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={editShiftDate}
                      onSelect={setEditShiftDate}
                      locale={de}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-shift-name" className="text-[#212529]">Name *</Label>
                <Input
                  id="edit-shift-name"
                  value={shiftName}
                  onChange={(e) => setShiftName(e.target.value)}
                  className="border-gray-300 focus:border-[#17c671]"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-shift-location" className="text-[#212529]">Einsatzort</Label>
                <Input
                  id="edit-shift-location"
                  placeholder="z.B. Bar 1, Obere Bar"
                  value={shiftLocation}
                  onChange={(e) => setShiftLocation(e.target.value)}
                  className="border-gray-300 focus:border-[#17c671]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-shift-position" className="text-[#212529]">Position</Label>
                <Input
                  id="edit-shift-position"
                  value={shiftPosition}
                  onChange={(e) => setShiftPosition(e.target.value)}
                  className="border-gray-300 focus:border-[#17c671]"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-shift-start" className="text-[#212529]">Startzeit</Label>
                <Input
                  id="edit-shift-start"
                  type="time"
                  value={shiftStartTime}
                  onChange={(e) => setShiftStartTime(e.target.value)}
                  className="border-gray-300 focus:border-[#17c671]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-shift-end" className="text-[#212529]">Endzeit</Label>
                <Input
                  id="edit-shift-end"
                  type="time"
                  value={shiftEndTime}
                  onChange={(e) => setShiftEndTime(e.target.value)}
                  className="border-gray-300 focus:border-[#17c671]"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-shift-staff" className="text-[#212529]">Benötigtes Personal *</Label>
              <Input
                id="edit-shift-staff"
                type="number"
                min="1"
                value={shiftRequiredStaff}
                onChange={(e) => setShiftRequiredStaff(e.target.value)}
                className="border-gray-300 focus:border-[#17c671]"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-shift-desc" className="text-[#212529]">Beschreibung</Label>
              <Textarea
                id="edit-shift-desc"
                value={shiftDescription}
                onChange={(e) => setShiftDescription(e.target.value)}
                rows={2}
                className="border-gray-300 focus:border-[#17c671]"
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => { setEditOpen(false); setEditingShift(null); resetShiftForm(); }} className="border-gray-300">
                Abbrechen
              </Button>
              <Button type="submit" className="bg-[#17c671] hover:bg-[#14a85d] text-white font-semibold" disabled={creating}>
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Speichern
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open && !deletingShiftId) setShiftToDelete(null);
        }}
      >
        <DialogContent className="sm:max-w-md bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#212529]">Veranstaltung löschen?</DialogTitle>
            <DialogDescription className="text-gray-500">
              {shiftToDelete?.date
                ? `${shiftToDelete.name} am ${format(parseISO(shiftToDelete.date), "dd.MM.yyyy", { locale: de })}`
                : "Diese Aktion kann nicht rückgängig gemacht werden."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              className="border-gray-300"
              onClick={() => {
                if (deletingShiftId) return;
                setDeleteDialogOpen(false);
                setShiftToDelete(null);
              }}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={handleDeleteShift}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={Boolean(deletingShiftId)}
              data-testid="confirm-delete-shift-btn"
            >
              {deletingShiftId ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Löschen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* View Assignments Dialog */}
      <Dialog open={assignmentsOpen} onOpenChange={setAssignmentsOpen}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#212529]">Zusagen für {selectedShift?.name}</DialogTitle>
            <DialogDescription className="text-gray-500">
              {selectedShift?.date && format(parseISO(selectedShift.date), "d. MMMM yyyy", { locale: de })}
              {(selectedShift?.start_time || selectedShift?.event_start) && ` - ${selectedShift?.start_time || selectedShift?.event_start}`}
              {(selectedShift?.end_time || selectedShift?.event_end) && ` - ${selectedShift?.end_time || selectedShift?.event_end}`}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-gray-500">
                {assignments.length} von {selectedShift?.required_staff} benötigten Mitarbeitern
              </span>
              {getStatusBadge(selectedShift || {})}
            </div>
            
            {loadingAssignments ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#17c671]" />
              </div>
            ) : assignments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>Noch keine Zusagen</p>
              </div>
            ) : (
              <div className="space-y-2">
                {assignments.map((assignment) => (
                  <div key={assignment.id} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{assignment.user_name}</p>
                      <p className="text-sm text-slate-500">{assignment.user_email}</p>
                    </div>
                  </div>
                    {assignment.comment?.trim() ? (
                      <div className="mt-2 rounded-md border border-green-100 bg-white px-3 py-2">
                        <p className="text-xs font-medium text-gray-500">Kommentar</p>
                        <p className="text-sm text-slate-700 break-words">{assignment.comment}</p>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deletePlanDialogOpen}
        onOpenChange={(open) => {
          if (deletingPlan) return;
          setDeletePlanDialogOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-md bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#212529]">Dienstplan wirklich löschen?</DialogTitle>
            <DialogDescription className="text-gray-500">
              Dieser Schritt entfernt den Dienstplan dauerhaft inklusive Schichten und Zusagen.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              className="border-gray-300"
              onClick={() => setDeletePlanDialogOpen(false)}
              disabled={deletingPlan}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeleteDienstplan}
              disabled={deletingPlan}
              data-testid="confirm-delete-dienstplan-btn"
            >
              {deletingPlan ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Löschen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <AppFooter />
    </div>
  );
};

export default DienstplanPage;

